// Reads and writes the settlement-hold flag on a supplier's NetSuite vendor
// record, served by a Workato recipe behind the cloud gateway. Addressed by the
// vendor name, not the FCC MERCHANT_ID, which NetSuite rejects as a nonexistent
// record. Wire format is PascalCase (`PaymentHold`), matching the tax-engine and
// common-merchant endpoints rather than the UPPER_SNAKE_CASE the Snowflake-backed
// FCC endpoints use.
import { reactive } from 'vue'
import axios from 'axios'
import { API_ENDPOINTS } from '@/config/api.endpoints'
import type { AuthState } from '@/types/authState'
import type { NetSuiteVendor } from '@/types/fcc'
import { ApiError } from '@/util/ApiError'
import { normalizeError } from '@/util/errorNormalizer'

export enum API_STATES {
  INITIAL = 'INITIAL',
  LOADING = 'LOADING',
  COMPLETE = 'COMPLETE',
  ERROR = 'ERROR',
}

interface NetSuiteVendorApi {
  states: {
    GET_VENDOR: API_STATES
    UPDATE_HOLD_PAYMENT: API_STATES
  }
  error: ApiError | null
  getVendor(authState: AuthState, vendorName: string): Promise<NetSuiteVendor>
  updateHoldPayment(
    authState: AuthState,
    vendorName: string,
    holdPayment: boolean,
  ): Promise<NetSuiteVendor | null>
}

function authHeaders(authState: AuthState) {
  return {
    headers: {
      Authorization: `Bearer ${authState.user?.accessToken}`,
      'Content-Type': 'application/json',
    },
  }
}

export const netsuiteVendorApi: NetSuiteVendorApi = reactive({
  states: {
    GET_VENDOR: API_STATES.INITIAL,
    UPDATE_HOLD_PAYMENT: API_STATES.INITIAL,
  },
  error: null,

  getVendor(authState: AuthState, vendorName: string): Promise<NetSuiteVendor> {
    netsuiteVendorApi.states.GET_VENDOR = API_STATES.LOADING
    netsuiteVendorApi.error = null

    return axios
      .get<NetSuiteVendor>(
        API_ENDPOINTS.NETSUITE.VENDOR(vendorName),
        authHeaders(authState),
      )
      .then(({ data }) => {
        if (!data || typeof data.PaymentHold !== 'boolean') {
          throw new Error('Vendor response did not include PaymentHold')
        }
        netsuiteVendorApi.states.GET_VENDOR = API_STATES.COMPLETE
        return data
      })
      .catch(err => {
        netsuiteVendorApi.states.GET_VENDOR = API_STATES.ERROR
        const apiError = normalizeError(err)
        netsuiteVendorApi.error = apiError
        throw apiError
      })
  },

  /** A 204 is a valid answer, so the resolved value is nullable. */
  updateHoldPayment(
    authState: AuthState,
    vendorName: string,
    holdPayment: boolean,
  ): Promise<NetSuiteVendor | null> {
    netsuiteVendorApi.states.UPDATE_HOLD_PAYMENT = API_STATES.LOADING
    netsuiteVendorApi.error = null

    return axios
      .patch<NetSuiteVendor>(
        API_ENDPOINTS.NETSUITE.VENDOR(vendorName),
        { PaymentHold: holdPayment },
        authHeaders(authState),
      )
      .then(({ data }) => {
        netsuiteVendorApi.states.UPDATE_HOLD_PAYMENT = API_STATES.COMPLETE
        return data ?? null
      })
      .catch(err => {
        netsuiteVendorApi.states.UPDATE_HOLD_PAYMENT = API_STATES.ERROR
        const apiError = normalizeError(err)
        netsuiteVendorApi.error = apiError
        throw apiError
      })
  },
})
